import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, PasswordInput } from '@mlm/ui';
import { Header } from '../storefront/components/header';
import { Footer } from '../storefront/components/footer';
import { login, reactivateAccount } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { resolveLoginDestination } from '../lib/login-resume';
import { queryClient } from '../lib/query-client';
import { inactivityBlockedMessage } from '../lib/inactivity-block';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Set when the account exists, the password is right, and it is CLOSED. */
  const [archived, setArchived] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login({ identifier, password, totp: totp || undefined });
      setSession(res.accessToken, res.user);
      // One-shot virtual-checkout resume (flag-gated; `?next=` validation
      // included) — see lib/login-resume.ts. Any lookup failure inside degrades
      // to the plain next/dashboard landing, so login itself can't get stuck.
      navigate(await resolveLoginDestination(queryClient, params.get('next')));
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; days?: number } } })?.response
        ?.data;
      const msg = data?.error;
      if (msg === 'totp_required') {
        setNeedsTotp(true);
        setError('TOTP code required');
      } else if (msg === 'cashier_not_allowed_in_member_portal') {
        setError('Cashier accounts cannot use the member portal. Please use the POS app.');
      } else if (msg === 'account_blocked_inactivity') {
        setError(inactivityBlockedMessage(data));
      } else if (msg === 'account_archived') {
        // The server only says this once the password has checked out, so the
        // offer below is safe to show: nobody learns an account exists by
        // guessing an email address.
        setArchived(true);
        setError(null);
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setSubmitting(false);
    }
  }

  /** Reopen the account, then sign in with the credentials already typed. */
  async function onReactivate() {
    setError(null);
    setReactivating(true);
    try {
      await reactivateAccount({ identifier, password });
      setArchived(false);
      const res = await login({ identifier, password, totp: totp || undefined });
      setSession(res.accessToken, res.user);
      navigate(await resolveLoginDestination(queryClient, params.get('next')));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (msg === 'totp_required') {
        // Reactivation SUCCEEDED; the account now just needs its second factor.
        setArchived(false);
        setNeedsTotp(true);
        setError('TOTP code required');
      } else if (msg === 'account_erased') {
        setError(
          'This account was permanently closed at your request and cannot be reopened. Please sign up again or contact support.',
        );
        setArchived(false);
      } else {
        setError('Could not reopen this account. Please try again or contact support.');
      }
    } finally {
      setReactivating(false);
    }
  }

  return (
    <div className="omni-store flex min-h-screen flex-col bg-background">
      {/* `stickyTop` pins the util strip TOO, not just the logo/search row.
          Without it the strip carrying Login / Sign Up scrolls away — most
          visible in the packaged mobile app, where the viewport is short enough
          that the sign-in card alone can push it off. */}
      <Header stickyTop />
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-12">
        <Card className="w-full max-w-sm overflow-hidden">
          {/* 2px brand hairline at the card's top edge — reads the tenant's
            `--brand-gradient` (the shared green/blue .omni-hairline is kept for
            admin/cashier); un-themed, that var IS the default store orange. */}
          <div className="h-0.5" style={{ background: 'var(--brand-gradient)' }} />
          <CardHeader>
            <CardTitle>Member Sign in</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email or username</label>
                <Input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {needsTotp && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">TOTP code</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={totp}
                    onChange={(e) => setTotp(e.target.value)}
                    pattern="\d{6}"
                    required
                  />
                </div>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              {archived && (
                <div className="space-y-2 rounded-md border p-3">
                  <p className="text-sm font-medium">This account is closed.</p>
                  <p className="text-xs text-muted-foreground">
                    Nothing was deleted. Reopen it and you will be signed straight in.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={reactivating}
                    onClick={() => void onReactivate()}
                  >
                    {reactivating ? 'Reopening…' : 'Reactivate my account'}
                  </Button>
                </div>
              )}
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                New here?{' '}
                <a className="underline" href="/signup">
                  Create an account
                </a>
              </p>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
